import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ThreeColumnLayoutV2 } from 'sharedComponents/ThreeColumnLayoutV2';
import AssetBrowserPanel from './AssetBrowserPanel/AssetBrowserPanelContainer';
import { SelectionPanel } from './SelectionPanel/SelectionPanelContainer';
import { ChartView } from '../../PowertrainAssetDetails/CrossAssetAnalysis/ChartView';
import { conditionIndicesViewOptions, toggleButtonOptions } from '../ConditionIndicesOverviewV2/constants';
import { setConditionIndicesView as setConditionIndicesViewAction } from '../ConditionIndicesOverviewV2/actions';
import { getOffGraphSignals } from './selectors';
import './style.scss';

const CrossAssetAnalysisV2Component = () => {
    const dispatch = useDispatch();
    const { t: translate } = useTranslation();
    const [conditionIndicesView, setConditionIndicesView] = useState(conditionIndicesViewOptions.LIST);
    const resizeTimeoutRef = useRef(null);
    const offGraphSignals = useSelector(getOffGraphSignals);
    const activeTab = useSelector(({ chartView }) => chartView?.activeTab);
    // Off-graph signal keys are namespaced per tab (`${activeTab}|...`), so signals added
    // while on Trend must not count toward the empty state shown on Scatter/Histogram.
    const tabOffGraphSignalsCount = useMemo(
        () => (offGraphSignals || []).filter(s => s?.key?.startsWith(`${activeTab}|`)).length,
        [offGraphSignals, activeTab]
    );

    const handleSetConditionIndicesView = useCallback((view) => {
        setConditionIndicesView(view);
    }, []);

    useEffect(() => {
        dispatch(setConditionIndicesViewAction(conditionIndicesView));
    }, [conditionIndicesView, dispatch]);

    const triggerChartResize = useCallback((delay = 300) => {
        requestAnimationFrame(() => {
            globalThis.dispatchEvent(new Event('resize'));
        });

        if (resizeTimeoutRef.current) {
            clearTimeout(resizeTimeoutRef.current);
        }

        resizeTimeoutRef.current = setTimeout(() => {
            globalThis.dispatchEvent(new Event('resize'));
        }, delay);
    }, []);

    const handleLeftPanelCollapse = useCallback(() => {
        triggerChartResize(320);
    }, [triggerChartResize]);

    const handleAnalysisPanelCollapse = useCallback(() => {
        triggerChartResize(320);
    }, [triggerChartResize]);

    const handleResizeLeftPanel = useCallback(() => {
        if (resizeTimeoutRef.current) {
            clearTimeout(resizeTimeoutRef.current);
        }

        resizeTimeoutRef.current = setTimeout(() => {
            globalThis.dispatchEvent(new Event('resize'));
        }, 150);
    }, []);

    useEffect(() => {
        return () => {
            if (resizeTimeoutRef.current) {
                clearTimeout(resizeTimeoutRef.current);
            }
        };
    }, []);

    return (
        <div className='cross-asset-analysis-v2-wrapper'>
            <ThreeColumnLayoutV2
                mainLayoutclass='cross-asset-v2-layout'
                leftPanelHeader={<span className='cross-asset-v2-assets-title'>{translate('ABB.Powertrain.Frontend.crossAssetAnalysisAssetListLabel')}</span>}
                centerPanelHeader={<span className='cross-asset-v2-comparison-title'>{translate('ABB.Powertrain.Frontend.crossAssetAnalysisSignalComparisonSetLabel')}</span>}
                leftPanelDefaulSize={15}
                leftPanelMinSize={15}
                leftPanelMaxSize={25}
                leftPanelCollapsedSize={5}
                leftPanelContentPadding='0 0 0 16px'
                centerPanelDefaultSize={15}
                centerPanelMinSize={15}
                centerPanelMaxSize={25}
                centerPanelCollapsedSize={5}
                rightPanelDefaultSize={70}
                rightPanelMinSize={40}
                isForHierarchyView={false}
                isForConditionOverview={true}
                isCenterCollapsible={true}
                conditionIndicesView={conditionIndicesView}
                setConditionIndicesView={handleSetConditionIndicesView}
                activeTab={toggleButtonOptions.ASSETS}
                onLeftPanelCollapse={handleLeftPanelCollapse}
                onCenterPanelCollapse={handleAnalysisPanelCollapse}
                onResizeLeftPanel={handleResizeLeftPanel}
                onResizeCenterPanel={handleResizeLeftPanel}
                onResizeRightPanel={handleResizeLeftPanel}
            >
                <div className='cross-asset-v2-left-hierarchy'>
                    <AssetBrowserPanel conditionIndicesView={conditionIndicesView} />
                </div>
                <div className='cross-asset-v2-left-selection'>
                    <SelectionPanel />
                </div>
                <ChartView isComparisonView offGraphSignalsCount={tabOffGraphSignalsCount} />
            </ThreeColumnLayoutV2>
        </div>
    );
};

export default CrossAssetAnalysisV2Component;
